import type { ChangeEvent } from "react";
import { useCallback, useMemo, useState } from "react";
import { Box, Input as MuiInput } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { ReactComponent as SearchIcon } from "../../assets/svg/Search.svg";
import { LabelBox, Button } from "../../components";
import { colors } from "../../theme/colors";
import { useGetLesionsQuery, useDeleteLesionMutation } from "../../services/api";
import { CustomTable } from "../../components/DataTable";
import style from "./lesion.module.css";
import { CustomActionMenu } from "../../components/Menu";
import type { ActionItem } from "../../components/Menu";
import { ReactComponent as EditIcon } from "../../assets/svg/edit.svg";
import { ConfirmationDialog } from "../../components/Dialog";
import { getErrorMessage } from "../../utils/common";

type LesionAction = "edit" | "delete";

/**
 * Lesions listing page with search, edit and delete row actions.
 *
 * Notes:
 * - Lesions are hard-deleted rather than deactivated, so there is no status
 *   column, no reason prompt and no logs dialog.
 */
const Lesions = () => {
  const [deleteLesion] = useDeleteLesionMutation();
  const navigate = useNavigate();

  // all query parameters and pagination params
  const [queryParam] = useState({
    order_by_columns: "name",
    order: "Asc",
    is_detailed: true,
  });
  const { data: lesionData, isFetching } = useGetLesionsQuery(queryParam);

  // The lesion endpoint takes no search parameter, so filtering happens here
  // over the fetched rows rather than as part of the query.
  const [searchTerm, setSearchTerm] = useState("");

  const lesions: Lesion[] = useMemo(() => {
    const items: Lesion[] = lesionData?.items ?? [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return items;
    return items.filter((item) => item.name?.toLowerCase().includes(term));
  }, [lesionData, searchTerm]);

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedLesion, setSelectedLesion] = useState<Lesion | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handles action menu selections for a lesion row.
   *
   * Args:
   *   value (LesionAction): Action selected from the row menu.
   *   item (Lesion): Lesion associated with the action.
   *
   * Returns:
   *   void
   */
  const handleMenu = useCallback(
    (value: LesionAction, item: Lesion) => {
      setSelectedLesion(item);
      switch (value) {
        case "edit":
          // Hand the row over so the edit screen does not have to re-fetch it.
          navigate(`/lesions/edit/${item?.id}`, { state: { lesion: item } });
          break;
        case "delete":
          setModalVisible(true);
          break;
      }
    },
    [navigate]
  );

  /**
   * Column definitions for the Lesions table.
   *
   * Returns:
   *   Array<object>: Column configuration for CustomTable.
   */
  const fields = useMemo(
    () => [
      { id: "name", label: "Lesion Name", flex: 2, sortable: true },
      {
        id: "actions",
        label: "Actions",
        type: "action",
        render: (item: Lesion) => (
          <CustomActionMenu<Lesion, LesionAction>
            item={item}
            isActive={item.is_active ?? false}
            handleChange={handleMenu}
            actions={
              [
                {
                  label: "Edit",
                  value: "edit",
                  icon: <EditIcon height={24} width={24} />,
                },
                {
                  label: "Delete",
                  value: "delete",
                  icon: <DeleteOutlineIcon height={24} width={24} />,
                },
              ] as ActionItem<LesionAction>[]
            }
          />
        ),
      },
    ],
    [handleMenu]
  );

  /**
   * Updates the search term in query params.
   *
   * Args:
   *   e (ChangeEvent<HTMLInputElement>): Search input change event.
   *
   * Returns:
   *   void
   */
  const handleSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    []
  );

  /**
   * Deletes the selected lesion.
   *
   * Returns:
   *   Promise<void>
   *
   * Notes:
   * - Deletion is permanent; the confirmation dialog is the only safeguard.
   */
  const handleDelete = async () => {
    try {
      // Guard against submitting without a selected row; avoids invalid deletes.
      if (!selectedLesion) {
        setMessage({
          type: "error",
          text: "Please select a lesion before deleting.",
        });
        return;
      }
      setIsLoading(true);
      await deleteLesion(selectedLesion.id).unwrap();
      setIsLoading(false);
      setModalVisible(false);
      setSelectedLesion(null);
    } catch (error: unknown) {
      setIsLoading(false);
      setModalVisible(false);
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Something went wrong!"),
      });
    }
  };

  // Render the Lesion component
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "15px",
        }}
      >
        <MuiInput
          disableUnderline
          placeholder="Search by name...."
          type="search"
          startAdornment={<SearchIcon width={20} height={20} />}
          value={searchTerm}
          onChange={handleSearch}
          sx={{ width: "550px", gap: "8px", borderRadius: "50px", backgroundColor: colors.white }}
        />
        <Link to="add">
          <Button
            variant="contained"
            className={style.addButton}
            label="Add New Lesion"
            sx={{
              ":hover": { backgroundColor: colors.primary },
            }}
          />
        </Link>
      </Box>
      {message && (
        <Box mt={2}>
          <LabelBox
            boxProps={{ height: 50, display: "flex", alignItems: "center" }}
            data={message}
            onClose={() => setMessage(null)}
          />
        </Box>
      )}
      <Box style={{ maxHeight: "calc(100vh - 195px)", overflow: "auto" }}>
        <CustomTable<Lesion>
          fields={fields}
          paginationRequired={true}
          data={lesions}
          loading={isFetching}
          onDoubleClick={(lesion: Lesion) => {
            navigate(`/lesions/edit/${lesion?.id}`, { state: { lesion } });
          }}
          defaultOrderBy="name"
          searchTerm={searchTerm}
          onClearSearch={() => setSearchTerm("")}
        />
      </Box>
      {modalVisible && (
        <ConfirmationDialog
          visible={modalVisible}
          message={`Are you sure you want to delete "${selectedLesion?.name}"? This cannot be undone.`}
          loading={isLoading}
          onClose={() => {
            setModalVisible(false);
          }}
          onConfirm={handleDelete}
        />
      )}
    </Box>
  );
}
export default Lesions;
